import React, { useLayoutEffect, useRef } from "react";
import styled from "styled-components";
import {
  TILES,
  SPRITE_SHEET_URL,
  SPRITE_CELL_WIDTH,
  SPRITE_CELL_HEIGHT,
} from "../Tile/tiles";
import { DEFAULT_TILE_WIDTH, DEFAULT_TILE_HEIGHT } from "../Tile/Tile";
import {
  TRANSLATE_RIGHT,
  TRANSLATE_DOWN,
  numberOfCardsInStack,
} from "../Foundation/Foundation";

export const DEFAULT_PARAMS = {
  // SIM time advanced per physics+draw step (steps tick at a fixed
  // 60Hz wall cadence — see STEP_WALL_MS). Higher = faster flight,
  // chunkier trail.
  stepMs: 3,
  gravity: 35000,
  launchVyMin: 500,
  launchVyMax: 1200,
  launchVxMin: 900,
  launchVxMax: 1800,
  rightwardChance: 0.35,
  bounceDamping: 0.8,
};

const FUDGE = 1;

// Deterministic PRNG (mulberry32). Seeded from the game id, the whole
// cascade becomes a pure function of (game, elapsed sim time): the
// winner, every spectator window, and the All Active Games slot all
// watch the SAME show, and any viewer can rebuild the exact trail by
// fast-stepping the sim (every step draws).
function mulberry32(a) {
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function WinCelebration({
  active,
  foundations,
  foundationRefs,
  // Deterministic cascade seed — pass the game id. Omitted falls back
  // to a random show.
  seed,
  // performance.now-domain moment the show STARTED. Omitted/now means
  // "from the top"; a value in the past means this viewer joined a show
  // already in flight — the sim fast-forwards the missed time in one
  // burst before the first frame, landing on the exact frame every
  // other viewer is on.
  startedAtMs = null,
  onPopTop,
  // Fired once when the cascade runs out of cards naturally (never on
  // unmount) — a full 52-card run is minutes long, so anything waiting
  // on "the show is over" must be told, not timed.
  onDone,
  params,
}) {
  const canvasRef = useRef(null);

  // Live-tweak escape hatch: effect captures these via refs so mid-run changes
  // (from a future settings window) take effect without restarting the animation.
  const paramsRef = useRef();
  paramsRef.current = { ...DEFAULT_PARAMS, ...params };
  const foundationsRef = useRef();
  foundationsRef.current = foundations;
  const onPopTopRef = useRef();
  onPopTopRef.current = onPopTop;
  const onDoneRef = useRef();
  onDoneRef.current = onDone;

  useLayoutEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Size the bitmap to match the canvas's visual pixel footprint so what we
    // draw lines up 1:1 with what we measure via getBoundingClientRect.
    const canvasRect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.round(canvasRect.width));
    canvas.height = Math.max(1, Math.round(canvasRect.height));

    // Infer zoom from the canvas itself (visual px / layout px).
    const zoom = canvas.offsetWidth ? canvasRect.width / canvas.offsetWidth : 1;
    // The sim runs entirely in LAYOUT px — the ~620x435 window space
    // every surface shares — and zoom applies only at draw time (the
    // ctx transform below). Physics in visual px would make scaled
    // copies (e.g. All Active Games slots) play the show faster: same
    // absolute velocities across a smaller canvas means cards exit
    // sooner. Layout space keeps the cascade a single show everywhere.
    const layoutWidth = canvas.offsetWidth || canvasRect.width;
    const layoutHeight = canvas.offsetHeight || canvasRect.height;
    const cardWidth = DEFAULT_TILE_WIDTH;
    const cardHeight = DEFAULT_TILE_HEIGHT;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(zoom, 0, 0, zoom, 0, 0);
    ctx.imageSmoothingEnabled = false;

    let raf = null;
    let lastTick = null;
    let flying = null;
    let done = false;
    const rand =
      seed != null ? mulberry32(Math.floor(seed) ^ 0x5eed) : Math.random;
    const randRange = (min, max) => min + rand() * (max - min);
    // Round-robin index across foundations. Each increment pops one card
    // from whichever foundation is next in rotation, giving us rank-major
    // launch order (K from 0, K from 1, ..., then Q from 0, ...) as long
    // as all foundations start full.
    let launchIdx = 0;

    const img = new Image();

    function tryLaunchNext() {
      const stacks = foundationsRef.current || [];
      const n = stacks.length;
      if (n === 0) return false;

      // At most `n * (ranks per suit)` launches total. Guard with a generous
      // cap so a malformed state can't spin us forever.
      const cap = launchIdx + n * 20;
      while (launchIdx < cap) {
        const foundationIdx = launchIdx % n;
        launchIdx++;
        const stack = stacks[foundationIdx];
        if (!stack || stack.length === 0) continue;

        const topName = stack[stack.length - 1];
        const size = stack.length;

        const el = (foundationRefs.current || [])[foundationIdx];
        if (!el) continue;
        const r = el.getBoundingClientRect();
        // Re-measure the canvas per launch: the mount-time rect goes
        // stale if the window is dragged mid-cascade. Rects are visual
        // px; divide back to layout px, where the sim lives.
        const cRect = canvas.getBoundingClientRect();
        const baseX = (r.left - cRect.left) / zoom;
        const baseY = (r.top - cRect.top) / zoom;

        // The visually-topmost card in a Foundation stack is offset by
        // (topIdx * TRANSLATE_RIGHT, topIdx * TRANSLATE_DOWN) source px
        // from the wrapper's origin — already layout px.
        const topIdx = numberOfCardsInStack(size) - 1;
        const offsetX = topIdx * TRANSLATE_RIGHT;
        const offsetY = topIdx * TRANSLATE_DOWN;

        const p = paramsRef.current;
        const goRight = rand() < p.rightwardChance;
        const vxMag = randRange(p.launchVxMin, p.launchVxMax);
        const vyMag = randRange(p.launchVyMin, p.launchVyMax);
        flying = {
          name: topName,
          x: baseX + offsetX,
          y: baseY + offsetY,
          vx: (goRight ? 1 : -1) * vxMag,
          vy: -vyMag,
        };

        // Pop this card from the parent's foundation state so the next
        // rank is exposed underneath on re-render.
        if (onPopTopRef.current) onPopTopRef.current(foundationIdx);
        return true;
      }
      return false;
    }

    function step() {
      if (!flying && !tryLaunchNext()) {
        done = true;
        // Natural exhaustion only — the cleanup path sets `done` without
        // coming through here, so unmount never reports completion.
        if (onDoneRef.current) onDoneRef.current();
        return;
      }
      const p = paramsRef.current;
      const dt = p.stepMs / 1000;

      flying.vy += p.gravity * dt;
      flying.x += flying.vx * dt;
      flying.y += flying.vy * dt;

      const floor = layoutHeight - cardHeight + FUDGE;
      if (flying.y > floor) {
        flying.y = floor;
        flying.vy = -flying.vy * p.bounceDamping;
      }

      const [row, col] = TILES[flying.name];
      ctx.drawImage(
        img,
        col * SPRITE_CELL_WIDTH,
        row * SPRITE_CELL_HEIGHT,
        SPRITE_CELL_WIDTH,
        SPRITE_CELL_HEIGHT,
        Math.floor(flying.x),
        Math.floor(flying.y),
        cardWidth,
        cardHeight,
      );

      // Cards exit by drifting off the sides (bouncing keeps them above the floor).
      if (flying.x + cardWidth < 0 || flying.x > layoutWidth) {
        flying = null;
      }
    }

    // One physics step per 60th of a second of WALL time, regardless of
    // display refresh rate — pins the tuned look to 60Hz everywhere
    // (one step per rAF would run 2x speed on a 120Hz screen).
    const STEP_WALL_MS = 1000 / 60;
    function advance(now, cap) {
      if (lastTick == null) lastTick = now;
      let pending = Math.floor((now - lastTick) / STEP_WALL_MS);
      if (pending <= 0) return;
      if (pending > cap) {
        // Over-cap backlog is dropped, not burst into one frame.
        pending = cap;
        lastTick = now - cap * STEP_WALL_MS;
      }
      lastTick += pending * STEP_WALL_MS;
      while (pending-- > 0 && !done) step();
    }

    function frame(now) {
      if (done) return;
      raf = requestAnimationFrame(frame);
      advance(now, 8);
    }

    // A hidden tab gets no rAF, so a timer advances the sim instead
    // while hidden, catching up in full each tick so the show stays in
    // real wall time. The 3600-step cap (one minute of sim) bounds a
    // pathological stall without falling behind even minute-granularity
    // throttling.
    let hiddenTimer = null;
    function onVisibility() {
      if (document.hidden) {
        if (hiddenTimer == null && !done)
          hiddenTimer = setInterval(() => {
            if (!done) advance(performance.now(), 3600);
          }, 250);
      } else if (hiddenTimer != null) {
        clearInterval(hiddenTimer);
        hiddenTimer = null;
      }
    }
    document.addEventListener("visibilitychange", onVisibility);

    function onLoad() {
      if (startedAtMs != null) {
        // Seed the step clock at the win moment and let advance() catch
        // up in one burst — every step draws, so the skipped steps
        // rebuild the exact trail (deterministic per seed). Cap =
        // exactly the backlog, so nothing is dropped.
        const now = performance.now();
        lastTick = Math.min(now, startedAtMs);
        advance(now, Math.floor((now - lastTick) / STEP_WALL_MS) + 1);
      }
      raf = requestAnimationFrame(frame);
      onVisibility();
    }

    img.addEventListener("load", onLoad, { once: true });
    img.src = SPRITE_SHEET_URL;

    return () => {
      img.removeEventListener("load", onLoad);
      document.removeEventListener("visibilitychange", onVisibility);
      if (hiddenTimer != null) clearInterval(hiddenTimer);
      if (raf != null) cancelAnimationFrame(raf);
      done = true;
    };
  }, [active, foundationRefs, seed, startedAtMs]);

  return <Canvas ref={canvasRef} />;
}

const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
  pointer-events: none;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
  image-rendering: pixelated;
`;

export default WinCelebration;
